/**
 * Hook for loading diagram data using React Query
 */

import { useQuery } from "@tanstack/react-query";

import { defaultConnector } from "../connectors";
import { DiagramConnector, DiagramState } from "../types";

interface UseDiagramDataOptions {
    /** Custom connector (defaults to environment-based connector) */
    connector?: DiagramConnector;
    /** Whether to enable the query */
    enabled?: boolean;
}

interface UseDiagramDataResult {
    /** Transformed diagram state ready for CC components */
    data: DiagramState | undefined;
    /** Loading state */
    isLoading: boolean;
    /** Error state */
    isError: boolean;
    /** Error object */
    error: Error | null;
    /** Refetch function */
    refetch: () => void;
}

/**
 * Hook for loading diagram data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useDiagramData(integrationId);
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 *
 * return <ConnectionCreatorDiagram initialNodes={data.nodes} initialEdges={data.edges} />;
 * ```
 */
export const useDiagramData = (
    diagramId: string | undefined,
    options: UseDiagramDataOptions = {}
): UseDiagramDataResult => {
    const { connector = defaultConnector, enabled = true } = options;

    const query = useQuery({
        queryKey: ["cc-diagram", diagramId],
        queryFn: async () => {
            if (!diagramId) {
                throw new Error("Diagram ID is required");
            }
            // Connector now returns DiagramState directly (already transformed)
            return connector.load(diagramId);
        },
        enabled: enabled && !!diagramId,
        staleTime: 0, // Always fetch fresh data
        gcTime: 0, // Don't cache data
        retry: 1,
    });

    return {
        data: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
};
